import React, { useState } from 'react';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    enquiry: ''
  });
  const [status, setStatus] = useState('idle'); // idle, submitting, success, error

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');

    try {
      const response = await fetch('https://formspree.io/f/mvzyapzb', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          enquiry: formData.enquiry
        })
      });

      if (response.ok) {
        setStatus('success');
        // Clear form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          enquiry: ''
        });
        // Reset success message after 5 seconds
        setTimeout(() => setStatus('idle'), 5000);
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <>
      <style>{`
        .contact-section {
          background: var(--cream);
          padding: 100px 48px;
        }

        .contact-container {
          max-width: 800px;
          margin: 0 auto;
        }

        .contact-heading {
          text-align: center;
          margin-bottom: 60px;
        }

        .contact-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 48px;
          font-weight: 300;
          color: var(--espresso);
          margin-bottom: 16px;
          line-height: 1.2;
        }

        .contact-subtitle {
          font-size: 16px;
          color: var(--mocha);
          line-height: 1.6;
          font-weight: 300;
        }

        .contact-form {
          background: var(--cream);
          padding: 48px;
          border-radius: 0;
          border: 1px solid var(--warm-stone);
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
          margin-bottom: 24px;
        }

        .form-group {
          margin-bottom: 24px;
        }

        .form-label {
          display: block;
          font-size: 12px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--espresso);
          margin-bottom: 8px;
          font-weight: 500;
        }

        .form-input,
        .form-textarea {
          width: 100%;
          padding: 14px 16px;
          font-size: 14px;
          font-family: 'DM Sans', sans-serif;
          color: var(--espresso);
          background: white;
          border: 1px solid var(--warm-stone);
          transition: all 0.3s ease;
          outline: none;
        }

        .form-input:focus,
        .form-textarea:focus {
          border-color: var(--warm-gold);
          box-shadow: 0 0 0 3px rgba(196, 168, 130, 0.1);
        }

        .form-textarea {
          min-height: 150px;
          resize: vertical;
        }

        .form-button {
          width: 100%;
          padding: 16px 32px;
          font-size: 11px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          font-weight: 600;
          background: var(--espresso);
          color: var(--warm-gold);
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: 'DM Sans', sans-serif;
        }

        .form-button:hover:not(:disabled) {
          background: var(--warm-gold);
          color: var(--espresso);
        }

        .form-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .form-footer-text {
          margin-top: 20px;
          font-size: 13px;
          color: var(--mocha);
          text-align: center;
          line-height: 1.6;
          font-weight: 300;
        }

        .success-message {
          margin-top: 20px;
          padding: 16px;
          background: rgba(196, 168, 130, 0.1);
          border-left: 3px solid var(--warm-gold);
          color: var(--warm-gold);
          font-size: 14px;
          text-align: center;
          font-weight: 500;
        }

        .error-message {
          margin-top: 20px;
          padding: 16px;
          background: rgba(255, 0, 0, 0.05);
          border-left: 3px solid #d32f2f;
          color: #d32f2f;
          font-size: 14px;
          text-align: center;
        }

        @media (max-width: 768px) {
          .contact-section {
            padding: 60px 24px;
          }

          .contact-title {
            font-size: 36px;
          }

          .contact-subtitle {
            font-size: 14px;
          }

          .contact-form {
            padding: 32px 24px;
          }

          .form-row {
            grid-template-columns: 1fr;
            gap: 0;
          }

          .form-group {
            margin-bottom: 20px;
          }
        }
      `}</style>

      <section className="contact-section" id="contact">
        <div className="contact-container">
          <div className="contact-heading">
            <h2 className="contact-title">Get In Touch</h2>
            <p className="contact-subtitle">
              We would love to hear from you. Fill in the form below and we will be in touch shortly.
            </p>
          </div>

          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName" className="form-label">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  className="form-input"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="lastName" className="form-label">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  className="form-input"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                className="form-input"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="enquiry" className="form-label">Details of Enquiry</label>
              <textarea
                id="enquiry"
                name="enquiry"
                className="form-textarea"
                value={formData.enquiry}
                onChange={handleChange}
                required
              />
            </div>

            <button 
              type="submit" 
              className="form-button"
              disabled={status === 'submitting'}
            >
              {status === 'submitting' ? 'Sending...' : 'Submit Enquiry'}
            </button>

            <p className="form-footer-text">
              Thank you for reaching out. A member of our team will be in touch with you as soon as possible.
            </p>

            {status === 'success' && (
              <div className="success-message">
                Thank you for your enquiry — we will be in touch with you very soon.
              </div>
            )}

            {status === 'error' && (
              <div className="error-message">
                Something went wrong. Please try again or email us directly at stephanieluxesolutions@gmail.com
              </div>
            )}
          </form>
        </div>
      </section>
    </>
  );
};

export default ContactForm;
